import React from 'react';
import { View, Text } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName } from '../../shared/utils/user';
import { PropertyPartner } from '../../shared/types/property';
import { Card, Button } from '../ui';

interface PropertyOwnershipProps {
  partners: PropertyPartner[];
  totalOwnership: number;
  currentUserId?: number;
  onAddPartner: () => void;
  onEditOwnership: (partner: PropertyPartner) => void;
  onRemovePartner: (partner: PropertyPartner) => void;
}

export function PropertyOwnership({ 
  partners, 
  totalOwnership, 
  currentUserId,
  onAddPartner, 
  onEditOwnership, 
  onRemovePartner 
}: PropertyOwnershipProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile } = useResponsive();

  return (
    <Card style={{ 
      marginBottom: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.xl,
      paddingBottom: isMobile ? styles.utils.spacing.xl : undefined,
    }}>
      <View style={{ 
        marginBottom: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg,
      }}>
        <View style={{ 
          flexDirection: 'row',
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: styles.utils.spacing.sm,
        }}>
          <Text style={[styles.sectionTitle, { 
            fontSize: isSmallMobile ? styles.utils.typography.sizes.base : undefined,
          }]}>
            Ownership
          </Text>
          <Text style={[styles.text(isSmallMobile ? 'xs' : 'sm'), { 
            color: totalOwnership === 100 
              ? styles.tokens.color.success 
              : addColorOpacity(styles.tokens.color.onBackground, 0.6),
            fontWeight: styles.utils.typography.weights.semibold,
          }]}>
            {totalOwnership.toFixed(1)}% allocated
          </Text>
        </View>
        <Button
          title={isSmallMobile ? "+ Partner" : "+ Add Partner"}
          onPress={onAddPartner}
          variant="primary"
          fullWidth={isMobile}
          size="sm"
        />
      </View>

      {totalOwnership !== 100 && (
        <View style={{
          padding: styles.utils.spacing.md,
          borderRadius: styles.utils.radius.md,
          backgroundColor: addColorOpacity(styles.tokens.color.error, 0.1),
          borderWidth: 1,
          borderColor: addColorOpacity(styles.tokens.color.error, 0.3),
          marginBottom: styles.utils.spacing.md,
        }}>
          <Text style={[styles.text('sm'), { 
            color: styles.tokens.color.error,
            fontWeight: styles.utils.typography.weights.medium,
          }]}>
            ⚠️ Total ownership is {totalOwnership.toFixed(1)}%. It should equal 100%.
          </Text>
        </View>
      )}

      {partners.length === 0 ? (
        <View style={{
          padding: styles.utils.spacing.xl,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Text style={[styles.text('base'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.6),
            textAlign: 'center',
          }]}>
            No partners assigned.{'\n'}Add partners to set ownership.
          </Text>
        </View>
      ) : (
        <View style={{ gap: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md }}>
          {partners.map((partner) => (
            <Card key={partner.user_id} elevated style={{ 
              marginBottom: 0,
              padding: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
            }}>
              {/* Partner info row */}
              <View style={{ 
                flexDirection: 'row',
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
              }}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text 
                    style={[styles.text(isSmallMobile ? 'sm' : 'base'), { 
                      fontWeight: styles.utils.typography.weights.semibold, 
                    }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {getDisplayName(partner)}
                  </Text>
                </View>
                <Text style={[styles.heading(isSmallMobile ? 'base' : 'lg'), { 
                  fontWeight: styles.utils.typography.weights.bold, 
                  color: styles.tokens.color.primary,
                  marginLeft: styles.utils.spacing.sm,
                }]}>
                  {partner.ownership_percentage}%
                </Text>
              </View>
              
              {/* Action buttons */}
              <View style={{ 
                flexDirection: 'row',
                gap: styles.utils.spacing.sm,
              }}>
                <Button
                  title="Edit"
                  onPress={() => onEditOwnership(partner)}
                  variant="outline"
                  size="sm"
                  style={{ flex: 1 }}
                />
                {currentUserId !== partner.user_id && (
                  <Button
                    title="Remove"
                    onPress={() => onRemovePartner(partner)}
                    variant="danger"
                    size="sm"
                    style={{ flex: 1 }}
                  />
                )}
              </View>
            </Card>
          ))}
        </View>
      )}
    </Card>
  );
}

